import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import ContactsIcon from "@mui/icons-material/Contacts";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { apiCreateJournal } from "../../graphQL/journal";
import { apiGetContactsCollection } from "../../graphQL/addressbook/collections/contacts";
import { apiGetPropertyAssetsCollection, apiGetPropertyAsset } from "../../graphQL/properties/collections/propertyAssets";

/* One reminder-creation dialog for the whole app. The mode (Contact /
   Property / Global) is always explicit and visible: callers pass the
   context's defaults (defaultMode/defaultRef) but the user always sees, and
   can change, what the reminder will attach to before saving. */

const MODES = [
  { value: "contact", label: "Contact", icon: <ContactsIcon sx={{ fontSize: 14 }} /> },
  { value: "property", label: "Property", icon: <ApartmentIcon sx={{ fontSize: 14 }} /> },
  { value: "global", label: "Global", icon: <PublicIcon sx={{ fontSize: 14 }} /> },
];

const MODE_COLLECTION = { contact: "contacts", property: "propertyAssets" };
const GLOBAL_REF_COLLECTION = "global";

const contactLabel = (c) => {
  const person = c?.identity?.person || {};
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || c?.identity?.company?.name || "";
};

// Canonical contact ids are UUID-shaped; legacy 24-hex ObjectIds on
// propertyAssets.contactList are dangling references and must be skipped.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// Route-based creation defaults: on the contact page the selected contact is
// the default target, on the property page the selected property. One
// derivation, shared by every dialog caller.
export const getReminderContextDefaults = (domain) => {
  const suffix = (domain && domain !== "All" ? domain : "") || "all";
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const read = (key) => {
    try { return window.localStorage.getItem(key); } catch { return null; }
  };
  if (path.startsWith("/properties")) {
    const pid = read(`contextstory.properties.selectedPropertyId:${suffix}`);
    return { mode: "property", ref: pid ? { collection: "propertyAssets", id: pid } : null };
  }
  if (path.startsWith("/addressbook")) {
    const cid = read(`contextstory.addressbook.selectedContactId:${suffix}`);
    return { mode: "contact", ref: cid ? { collection: "contacts", id: cid } : null };
  }
  return { mode: "global", ref: null };
};

const propertyLabel = (p) => {
  const a = p?.identity?.address || {};
  return [[a.streetNo, a.streetName].filter(Boolean).join(" "), a.city].filter(Boolean).join(", ");
};

function addDuration(baseDate, amount, unit) {
  const d = new Date(baseDate);
  const n = Number(amount);
  if (!n || n <= 0) return d;
  if (unit === "day") d.setDate(d.getDate() + n);
  if (unit === "week") d.setDate(d.getDate() + n * 7);
  if (unit === "month") d.setMonth(d.getMonth() + n);
  if (unit === "year") d.setFullYear(d.getFullYear() + n);
  return d;
}

function setTimeTo8am(date) {
  const d = new Date(date);
  d.setHours(8, 0, 0, 0);
  return d;
}

export default function ReminderCreateDialog({
  open,
  onClose,
  authorId,
  domain,
  defaultMode = "global",
  defaultRef = null, // { collection, id, label? } — pre-fills the entity for its mode
  onCreated,
}) {
  const [mode, setMode] = useState(defaultMode);
  // One picked entity per linked mode so switching modes doesn't lose picks.
  const [picked, setPicked] = useState({ contact: null, property: null });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState(1);
  const [unit, setUnit] = useState("day");
  const [action, setAction] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  // Entities linked to the context entity (a contact's properties, a
  // property's contacts) offered as one-click picks when switching modes.
  const [linked, setLinked] = useState({ contact: [], property: [] });
  // Reach (phone/email) of the picked contact, fetched at pick time so the
  // call QR can render before the reminder is saved.
  const [pickedReach, setPickedReach] = useState({ id: null, phone: "", email: "" });
  const searchSeq = useRef(0);

  const scopeDomain = domain && domain !== "All" ? domain : "";

  // Re-seed from the caller's context each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setMode(defaultMode);
    setQuery("");
    setResults([]);
    const coll = defaultRef?.collection;
    const seeded = { contact: null, property: null };
    if (coll === "contacts" && defaultRef?.id) seeded.contact = { id: defaultRef.id, label: defaultRef.label || "" };
    if (coll === "propertyAssets" && defaultRef?.id) seeded.property = { id: defaultRef.id, label: defaultRef.label || "" };
    setPicked(seeded);
    setLinked({ contact: [], property: [] });
  }, [open, defaultMode, defaultRef?.collection, defaultRef?.id, defaultRef?.label]);

  // Load the context entity's linked entities so switching modes offers them
  // directly (a contact may have several properties and vice versa). A single
  // linked entity is auto-picked — still shown and clearable, never silent.
  useEffect(() => {
    if (!open || !defaultRef?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (defaultRef.collection === "contacts") {
          const res = await apiGetContactsCollection({
            filters: { ids: [defaultRef.id], ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: 1 },
          });
          const ids = (res?.items?.[0]?.propertyListIds || []).filter(Boolean);
          if (!ids.length || cancelled) return;
          const props = await apiGetPropertyAssetsCollection({
            filters: { ids, ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: ids.length },
          });
          const options = (props?.items || []).map((p) => ({ id: p.id, label: propertyLabel(p) })).filter((o) => o.id);
          if (cancelled) return;
          setLinked((prev) => ({ ...prev, property: options }));
          if (options.length === 1) setPicked((prev) => (prev.property ? prev : { ...prev, property: options[0] }));
        } else if (defaultRef.collection === "propertyAssets") {
          const prop = await apiGetPropertyAsset(defaultRef.id);
          const p = prop?.propertyAsset || prop;
          const ids = [...new Set((p?.contactList || [])
            .map((e) => e?.contactId)
            .filter((id) => typeof id === "string" && UUID_RE.test(id)))];
          if (!ids.length || cancelled) return;
          const res = await apiGetContactsCollection({
            filters: { ids, ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: ids.length },
          });
          const options = (res?.items || []).map((c) => ({ id: c.id, label: contactLabel(c) })).filter((o) => o.id);
          if (cancelled) return;
          setLinked((prev) => ({ ...prev, contact: options }));
          if (options.length === 1) setPicked((prev) => (prev.contact ? prev : { ...prev, contact: options[0] }));
        }
      } catch { /* suggestions are best-effort; search still works */ }
    })();
    return () => { cancelled = true; };
  }, [open, defaultRef?.collection, defaultRef?.id, scopeDomain]);

  // Resolve a label for a pre-filled entity that arrived as a bare id.
  useEffect(() => {
    if (!open) return;
    const current = picked[mode];
    if (!current?.id || current.label) return;
    let cancelled = false;
    (async () => {
      try {
        if (mode === "contact") {
          const res = await apiGetContactsCollection({
            filters: { ids: [current.id], ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: 1 },
          });
          const c = res?.items?.[0];
          if (!cancelled && c) setPicked((prev) => ({ ...prev, contact: { ...prev.contact, label: contactLabel(c) } }));
        } else if (mode === "property") {
          const res = await apiGetPropertyAsset(current.id);
          const p = res?.propertyAsset || res;
          if (!cancelled && p) setPicked((prev) => ({ ...prev, property: { ...prev.property, label: propertyLabel(p) } }));
        }
      } catch { /* label is cosmetic; the id remains authoritative */ }
    })();
    return () => { cancelled = true; };
  }, [open, mode, picked, scopeDomain]);

  // Fetch the picked contact's reach as soon as it is picked so the call QR
  // renders immediately (and save can reuse it without a second fetch).
  useEffect(() => {
    const id = mode === "contact" ? picked.contact?.id : null;
    if (!open || !id || pickedReach.id === id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGetContactsCollection({
          full: true,
          filters: { ids: [id], ...(scopeDomain ? { domain: scopeDomain } : {}) },
          pagination: { page: 1, limit: 1 },
        });
        const c = res?.items?.[0];
        const phone = (c?.reach?.phones || []).find((ph) => ph?.value?.trim())?.value || "";
        const email = (c?.reach?.emails || []).find((e) => e?.value?.trim())?.value || "";
        if (!cancelled) setPickedReach({ id, phone, email });
      } catch {
        if (!cancelled) setPickedReach({ id, phone: "", email: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, picked.contact?.id, pickedReach.id, scopeDomain]);

  // Debounced entity search for the active linked mode.
  useEffect(() => {
    if (!open || mode === "global" || !query.trim()) { setResults([]); return undefined; }
    const seq = ++searchSeq.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        let items = [];
        if (mode === "contact") {
          const res = await apiGetContactsCollection({
            filters: { text: query.trim(), ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: 8 },
          });
          items = (res?.items || []).map((c) => ({ id: c.id, label: contactLabel(c) }));
        } else {
          const res = await apiGetPropertyAssetsCollection({
            filters: { query: query.trim(), ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: 8 },
          });
          items = (res?.items || []).map((p) => ({ id: p.id, label: propertyLabel(p) }));
        }
        if (searchSeq.current === seq) setResults(items.filter((i) => i.id));
      } catch {
        if (searchSeq.current === seq) setResults([]);
      } finally {
        if (searchSeq.current === seq) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [open, mode, query, scopeDomain]);

  const pickedEntity = mode === "global" ? null : picked[mode];
  const needsEntity = mode !== "global" && !pickedEntity?.id;
  const canSave = !!authorId && !saving && !!amount && !!unit && !!action && !needsEntity;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      const dueDate = setTimeTo8am(addDuration(new Date(), Number(amount), unit)).toISOString();
      const ref = mode === "global"
        ? { collection: GLOBAL_REF_COLLECTION, id: String(authorId) }
        : { collection: MODE_COLLECTION[mode], id: pickedEntity.id };

      // Stamp the linked contact's identity so reminder cards can show who
      // the reminder is about without re-resolving the ref.
      let entityStamp = {};
      if (mode === "contact" && pickedReach.id === ref.id) {
        entityStamp = { contactId: ref.id, contactName: pickedEntity.label || "", contactEmail: pickedReach.email, contactPhone: pickedReach.phone };
      } else if (mode === "contact") {
        try {
          const res = await apiGetContactsCollection({
            full: true,
            filters: { ids: [ref.id], ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: 1 },
          });
          const c = res?.items?.[0];
          const email = (c?.reach?.emails || []).find((e) => e?.value?.trim())?.value || "";
          const phone = (c?.reach?.phones || []).find((ph) => ph?.value?.trim())?.value || "";
          entityStamp = { contactId: ref.id, contactName: contactLabel(c) || pickedEntity.label || "", contactEmail: email, contactPhone: phone };
        } catch {
          entityStamp = { contactId: ref.id, contactName: pickedEntity.label || "" };
        }
      } else if (mode === "property") {
        entityStamp = { refLabel: pickedEntity.label || "" };
      }

      await apiCreateJournal({
        authorId,
        ref,
        type: "reminder",
        dueDate,
        ...(scopeDomain ? { domain: scopeDomain } : {}),
        data: {
          amount: Number(amount),
          unit,
          action,
          text: text || "",
          url: url || "",
          scope: mode,
          ...entityStamp,
        },
      });
      onCreated?.({ mode, ref, label: pickedEntity?.label || "" });
      setAmount(1);
      setUnit("day");
      setAction("");
      setText("");
      setUrl("");
      onClose?.();
    } catch (err) {
      console.error("Failed to create reminder:", err);
    } finally {
      setSaving(false);
    }
  }, [canSave, amount, unit, action, text, url, mode, pickedEntity, pickedReach, authorId, scopeDomain, onCreated, onClose]);

  const inputUnderline = {
    fontSize: 13, fontWeight: 500, color: "#3730a3",
    border: "none", borderBottom: "2px solid #4f46e5",
    background: "transparent", outline: "none", padding: "2px 4px",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827", mb: 1 }}>
          New Reminder
        </Typography>

        {/* Mode selector (square) with the picked entity chip (oval) inline
            on the same row. The target is always explicit, never inferred
            silently. In an entity context the cross-mode only appears when
            linked entities exist (a contact with no properties gets no
            Property option, and vice versa). */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1 }}>
          {MODES.filter((m) => {
            if (m.value === "global") return true;
            if (defaultRef?.collection === "contacts" && m.value === "property") return linked.property.length > 0;
            if (defaultRef?.collection === "propertyAssets" && m.value === "contact") return linked.contact.length > 0;
            return true;
          }).map((m) => (
            <Button
              key={m.value}
              size="small"
              startIcon={m.icon}
              onClick={() => { setMode(m.value); setQuery(""); setResults([]); }}
              sx={{
                textTransform: "none", fontSize: 12, fontWeight: 600,
                px: 1.25, py: 0.375, borderRadius: "6px",
                border: "1px solid",
                ...(mode === m.value
                  ? { color: "#3730a3", borderColor: "#4f46e5", backgroundColor: "#eef2ff" }
                  : { color: "#64748b", borderColor: "#e2e8f0", backgroundColor: "#fff" }),
                "&:hover": { backgroundColor: mode === m.value ? "#e0e7ff" : "#f8fafc" },
              }}
            >
              {m.label}
            </Button>
          ))}
          {mode !== "global" && pickedEntity?.id && (
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.5,
              px: 1, py: 0.375, borderRadius: "999px",
              border: "1px solid #c7d2fe", backgroundColor: "#eef2ff",
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#3730a3" }}>
                {pickedEntity.label || pickedEntity.id}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPicked((prev) => {
                  const cur = prev[mode];
                  // Keep the cleared entity available as a one-click pill so
                  // deselecting is never a dead end.
                  if (cur?.id) {
                    setLinked((l) => (l[mode].some((r) => r.id === cur.id) ? l : { ...l, [mode]: [cur, ...l[mode]] }));
                  }
                  return { ...prev, [mode]: null };
                })}
                sx={{ p: 0.125 }}
                aria-label={`Clear selected ${mode}`}
              >
                <CloseIcon sx={{ fontSize: 13, color: "#64748b" }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Entity picker: suggestions + search, shown only while nothing is
            picked for the active mode. */}
        {mode !== "global" && !pickedEntity?.id && (
          <Box sx={{ mb: 1 }}>
            {linked[mode].length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                {linked[mode].map((r) => (
                  <Box
                    key={r.id}
                    onClick={() => { setPicked((prev) => ({ ...prev, [mode]: r })); setQuery(""); setResults([]); }}
                    sx={{
                      px: 1, py: 0.375, borderRadius: "999px", fontSize: 12, fontWeight: 600,
                      color: "#3730a3", border: "1px solid #c7d2fe", backgroundColor: "#fff",
                      cursor: "pointer", "&:hover": { backgroundColor: "#eef2ff" },
                    }}
                  >
                    {r.label || r.id}
                  </Box>
                ))}
              </Box>
            )}
            <>
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder={mode === "contact" ? "Search contacts..." : "Search properties..."}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searching ? (
                      <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                    ) : null,
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { height: 32, fontSize: 12 } }}
                />
                {results.length > 0 && (
                  <Box sx={{ mt: 0.5, border: "1px solid #e2e8f0", borderRadius: 1, overflow: "hidden" }}>
                    {results.map((r) => (
                      <Box
                        key={r.id}
                        onClick={() => { setPicked((prev) => ({ ...prev, [mode]: r })); setQuery(""); setResults([]); }}
                        sx={{
                          px: 1.25, py: 0.625, fontSize: 12, color: "#334155", cursor: "pointer",
                          "&:hover": { backgroundColor: "#eef2ff" },
                          "& + &": { borderTop: "1px solid #f1f5f9" },
                        }}
                      >
                        {r.label || r.id}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
          </Box>
        )}

        <Box sx={{ border: "1.5px dashed #c7d2fe", borderRadius: 1.5, p: 1.5, backgroundColor: "#f8fafc" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", lineHeight: 2 }}>
            <Typography component="span" sx={{ fontSize: 13, color: "#475569" }}>Set a reminder in</Typography>
            <input
              type="number"
              min="1"
              max="365"
              value={amount}
              onChange={(e) => setAmount(Math.max(1, Math.min(365, Number(e.target.value || 1))))}
              style={{ ...inputUnderline, width: 48, textAlign: "center" }}
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              style={{ ...inputUnderline, padding: "2px 20px 2px 4px" }}
            >
              <option value="day">days</option>
              <option value="week">weeks</option>
              <option value="month">months</option>
              <option value="year">years</option>
            </select>
            <Typography component="span" sx={{ fontSize: 13, color: "#475569" }}>to</Typography>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              style={{ ...inputUnderline, padding: "2px 20px 2px 4px" }}
            >
              <option value="" disabled>select action</option>
              <option value="call">call</option>
              <option value="email">email</option>
              <option value="text">text</option>
              <option value="schedule_meeting">schedule a meeting</option>
              <option value="attend_meeting">attend a meeting</option>
              <option value="meet_in_person">meet in person</option>
              <option value="follow_up">follow up</option>
            </select>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <input
              type="text"
              placeholder="Enter details..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              style={{
                width: "100%", boxSizing: "border-box",
                fontSize: 13, color: "#334155",
                border: "none", borderBottom: "1.5px solid #cbd5e1",
                background: "transparent", outline: "none", padding: "6px 0",
              }}
            />
            {(action === "attend_meeting" || action === "meet_in_person") && (
              <input
                type="url"
                placeholder="Enter url..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box", marginTop: 4,
                  fontSize: 13, color: "#334155",
                  border: "none", borderBottom: "1.5px solid #cbd5e1",
                  background: "transparent", outline: "none", padding: "6px 0",
                }}
              />
            )}
          </Box>

          {/* Call QR: scannable as soon as "call" is chosen for a contact
              with a phone number — no need to save the reminder first. */}
          {mode === "contact" && action === "call" && pickedEntity?.id &&
            pickedReach.id === pickedEntity.id && pickedReach.phone && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 1.5, py: 1.5, backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
              <QRCodeSVG value={`tel:${pickedReach.phone}`} size={140} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1e293b", letterSpacing: "0.02em" }}>
                {pickedReach.phone}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onClose}
              sx={{
                px: 1.5, py: 0.5, borderRadius: "6px", textTransform: "none",
                fontSize: 12, fontWeight: 600, color: "#475569", borderColor: "#cbd5e1",
                "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSave}
              disabled={!canSave}
              sx={{
                px: 1.5, py: 0.5, borderRadius: "6px", textTransform: "none",
                fontSize: 12, fontWeight: 600, color: "#fff",
                backgroundColor: "#4f46e5", boxShadow: "0 2px 6px rgba(79,70,229,0.3)",
                "&:hover": { backgroundColor: "#4338ca" },
                "&.Mui-disabled": { opacity: 0.5, backgroundColor: "#4f46e5", color: "#fff" },
              }}
            >
              {saving ? "Saving..." : needsEntity ? `Pick a ${mode}` : "Set Reminder"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
